import React, { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addModularHullSystem,
  removeModularHullSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { formatCurrency } from "../../../../utils/formatting";
import { selectors as optionalSystemsSelectors } from "../../../../store/selectors/optionalSystemsSelectors";
import { loadModuleDesign } from "../../../../utils/moduleSaveLoad";

import { selectors } from "../../../../store/selectors";

interface ModularHullSystemsProps {
  availableSystems: any[];
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const MAX_MODULAR_PERCENT = 75;
const MODULE_COST_PER_TON = 25000; // Cr

const ModularHullSystems: React.FC<ModularHullSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const existingConfig = useAppSelector(
    optionalSystemsSelectors.selectModularHullConfig,
  );

  // Use selectors for modular hull calculations
  const excludedTonnage = useAppSelector((state) =>
    selectors.derived.selectModularHullExcludedTonnage(state, shipTonnage),
  );
  const maxModularTonnage = useAppSelector((state) =>
    selectors.derived.selectMaxModularTonnage(state, shipTonnage),
  );

  // Local state for modular percentage and modules
  const [modularPercent, setModularPercent] = useState<number>(0);
  const [modules, setModules] = useState<
    {
      name: string;
      tonnage: number;
      installed: boolean;
      description?: string;
      fitoutCost?: number;
      fitoutWeight?: number;
      fitoutPower?: number;
      moduleConfig?: any;
    }[]
  >([]);
  const [installWarning, setInstallWarning] = useState<string>("");
  const [importStatus, setImportStatus] = useState<string>("");

  // Use selectors for calculated values
  const modularTonnage = useAppSelector((state) =>
    selectors.derived.selectModularTonnage(state, shipTonnage, modularPercent),
  );
  const increasedHullCost = useAppSelector((state) =>
    selectors.derived.selectModularHullCost(state, shipTonnage, modularPercent),
  );
  

  const totalInstalledTonnage = modules
    .filter((m) => m.installed)
    .reduce((sum, m) => sum + m.tonnage + (m.fitoutWeight || 0), 0);
  const totalInstalledCost = modules
    .filter((m) => m.installed)
    .reduce(
      (sum, m) => sum + m.tonnage * MODULE_COST_PER_TON + (m.fitoutCost || 0),
      0,
    );
  const totalModuleCost = modules.reduce(
    (sum, m) => sum + m.tonnage * MODULE_COST_PER_TON + (m.fitoutCost || 0),
    0,
  );

  // Load from Redux on mount
  useEffect(() => {
    if (existingConfig) {
      setModularPercent(existingConfig.modularPercent || 0);
      setModules(existingConfig.modules || []);
    }
  }, [existingConfig]);

  // Save to Redux
  const handleSaveConfig = () => {
    const config = {
      id: "modularHullConfig",
      type: "modularHull" as const,
      subtype: "modularHull",
      name: "Modular Hull Configuration",
      description: "Configures modular hull percentage and modules.",
      cost: increasedHullCost,
      tonnage: modularTonnage,
      power: 0,
      techLevel,
      properties: {},
      modularPercent,
      modularTonnage,
      increasedHullCost,
      modules,
    };
    dispatch(addModularHullSystem(config));
  };

  // Remove config from Redux
  const handleRemoveConfig = () => {
    dispatch(removeModularHullSystem("modularHullConfig"));
    setModularPercent(0);
    setModules([]);
  };

  // Import module from file
  const handleImportModule = async () => {
    setImportStatus("Loading module file...");
    try {
      const importedModule = await loadModuleDesign();
      
      if (importedModule) {
        // Validate imported module data
        if (!importedModule.name || !importedModule.tonnage) {
          setImportStatus("Error: Invalid module data - missing name or tonnage");
          return;
        }

        // Check if module tonnage fits within modular capacity
        if (importedModule.tonnage > modularTonnage) {
          setImportStatus(`Error: Module tonnage (${importedModule.tonnage}) exceeds modular capacity (${modularTonnage})`);
          return;
        }

        // Check if adding this module would exceed total modular tonnage
        const newTotalTonnage = totalInstalledTonnage + importedModule.tonnage;
        if (newTotalTonnage > modularTonnage) {
          setImportStatus(`Error: Total module tonnage (${newTotalTonnage}) would exceed modular capacity (${modularTonnage})`);
          return;
        }

        // Check if module name already exists
        const existingModuleNames = modules.map(m => m.name.toLowerCase());
        let moduleName = importedModule.name;
        let counter = 1;
        while (existingModuleNames.includes(moduleName.toLowerCase())) {
          moduleName = `${importedModule.name} (${counter})`;
          counter++;
        }

        // Add the imported module to the modules list with full data preservation
        const newModule = {
          name: moduleName,
          tonnage: importedModule.tonnage,
          installed: false,
          description: importedModule.description || "Module imported from file",
          fitoutCost: importedModule.fitoutCost || importedModule.moduleConfig?.totalCost || 0,
          fitoutWeight: importedModule.fitoutWeight || importedModule.moduleConfig?.totalWeight || 0,
          fitoutPower: importedModule.fitoutPower || importedModule.moduleConfig?.totalPower || 0,
          moduleConfig: {
            // Preserve all detailed module configuration
            optionalSystems: importedModule.moduleConfig?.optionalSystems || [],
            computer: importedModule.moduleConfig?.computer || [],
            computerOptions: importedModule.moduleConfig?.computerOptions || {},
            sensors: importedModule.moduleConfig?.sensors,
            software: importedModule.moduleConfig?.software || {
              installed: [],
              running: [],
              expertSkills: {},
              translatorLanguages: {},
              databaseTopics: {},
              virtualCrewRoles: {},
              virtualGunnerRoles: {},
            },
            weapons: importedModule.moduleConfig?.weapons || [],
            weaponMounts: importedModule.moduleConfig?.weaponMounts || [],
            defensiveSystems: importedModule.moduleConfig?.defensiveSystems || [],
            ammunitionStorage: importedModule.moduleConfig?.ammunitionStorage || [],
            drives: importedModule.moduleConfig?.drives || [],
            powerPlant: Array.isArray(importedModule.moduleConfig?.powerPlant) 
              ? importedModule.moduleConfig!.powerPlant 
              : (importedModule.moduleConfig?.powerPlant ? [importedModule.moduleConfig!.powerPlant] : []),
            crew: importedModule.moduleConfig?.crew,
            staterooms: importedModule.moduleConfig?.staterooms || [],
            stateroomConfig: importedModule.moduleConfig?.stateroomConfig,
            cargo: importedModule.moduleConfig?.cargo,
            fuel: importedModule.moduleConfig?.fuel,
            // Calculations and metadata
            totalCost: importedModule.moduleConfig?.totalCost || 0,
            totalWeight: importedModule.moduleConfig?.totalWeight || 0,
            totalPower: importedModule.moduleConfig?.totalPower || 0,
            remainingTons: importedModule.moduleConfig?.remainingTons || 0,
            crewRequirements: importedModule.moduleConfig?.crewRequirements || 0,
            totalCrew: importedModule.moduleConfig?.totalCrew || 0,
            totalPassengers: importedModule.moduleConfig?.totalPassengers || 0,
            monthlyCost: importedModule.moduleConfig?.monthlyCost || 0,
            // Additional metadata
            techLevel: importedModule.techLevel,
            supplyUnits: importedModule.supplyUnits,
            supplyTons: importedModule.supplyTons,
          },
        };

        setModules([...modules, newModule]);
        
        // Create detailed success message
        const moduleDetails = [];
        if (newModule.moduleConfig?.optionalSystems?.length > 0) {
          moduleDetails.push(`${newModule.moduleConfig.optionalSystems.length} optional systems`);
        }
        if (newModule.moduleConfig?.weapons?.length > 0) {
          moduleDetails.push(`${newModule.moduleConfig.weapons.length} weapons`);
        }
        if (newModule.moduleConfig?.computer?.length > 0) {
          moduleDetails.push(`${newModule.moduleConfig.computer.length} computers`);
        }
        if (newModule.moduleConfig?.software?.installed?.length > 0) {
          moduleDetails.push(`${newModule.moduleConfig.software.installed.length} software packages`);
        }
        
        const detailsText = moduleDetails.length > 0 ? ` (${moduleDetails.join(', ')})` : '';
        setImportStatus(`Successfully imported module: ${newModule.name}${detailsText}`);
        
        // Clear success message after 5 seconds (longer for detailed message)
        setTimeout(() => setImportStatus(""), 5000);
      } else {
        setImportStatus("No module file selected or import cancelled");
        setTimeout(() => setImportStatus(""), 3000);
      }
    } catch (error) {
      console.error("Error importing module:", error);
      setImportStatus("Error importing module. Please try again.");
      setTimeout(() => setImportStatus(""), 5000);
    }
  };

  // Handlers
  const handlePercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = Number(e.target.value);
    if (value > MAX_MODULAR_PERCENT) value = MAX_MODULAR_PERCENT;
    if (value < 0) value = 0;
    setModularPercent(value);
    // Remove modules if over new modular tonnage
    setModules((prev) =>
      prev.filter((m) => m.tonnage <= Math.round((shipTonnage * value) / 100)),
    );
  };
  const handleAddModule = () => {
    setModules([
      ...modules,
      { name: `Module ${modules.length + 1}`, tonnage: 1, installed: false },
    ]);
  };
  const handleModuleTonnageChange = (idx: number, ton: number) => {
    if (ton < 0) ton = 0;
    setModules(modules.map((m, i) => (i === idx ? { ...m, tonnage: ton } : m)));
  };
  const handleRemoveModule = (idx: number) => {
    setModules(modules.filter((_, i) => i !== idx));
  };
  const handleInstallModule = (idx: number) => {
    const mod = modules[idx];
    const newTotal = totalInstalledTonnage + mod.tonnage;
    if (newTotal > modularTonnage) {
      setInstallWarning(
        `Cannot install: total installed module tonnage (${newTotal}) exceeds modular tonnage limit (${modularTonnage}).`,
      );
      return;
    }
    setInstallWarning("");
    setModules(
      modules.map((m, i) => (i === idx ? { ...m, installed: true } : m)),
    );
  };
  const handleUninstallModule = (idx: number) => {
    setInstallWarning("");
    setModules(
      modules.map((m, i) => (i === idx ? { ...m, installed: false } : m)),
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Modular Hull</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Modular Hull Configuration
        </h3>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Modular Percentage (max {MAX_MODULAR_PERCENT}%):
            <input
              type="number"
              min={0}
              max={MAX_MODULAR_PERCENT}
              value={modularPercent}
              onChange={handlePercentChange}
              className="ml-2 w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
            />
            %
          </label>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Modular Tonnage: <b>{modularTonnage}</b> / {maxModularTonnage} tons
            (Excludes bridge, power plant, drives, structure, armour)
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Increased Hull Cost: <b>{formatCurrency(increasedHullCost)}</b>
          </span>
        </div>
        {shipTonnage - excludedTonnage <= 0 && (
          <div className="text-red-600 text-sm mt-2">
            Not enough available tonnage for modular hull after exclusions.
          </div>
        )}
        {modularTonnage > maxModularTonnage && (
          <div className="text-red-600 text-sm mt-2">
            Selected modular percentage exceeds allowed modular tonnage after
            exclusions.
          </div>
        )}
        
        {/* Import Status Message */}
        {importStatus && (
          <div className={`mt-2 p-2 rounded text-sm ${
            importStatus.startsWith("Error") 
              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700" 
              : importStatus.startsWith("Successfully") 
                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700"
                : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700"
          }`}>
            {importStatus}
          </div>
        )}

        <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Modules</h4>
        
        {/* Module Import Button */}
        <div className="mb-4">
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 mr-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            onClick={handleImportModule}
            disabled={modularPercent === 0}
            title="Import a module design from a saved file (.TM2 format)"
          >
            Import Module from File {modularPercent === 0 ? "(Disabled - Set modular percentage first)" : ""}
          </button>
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Supports .TM2 module files and ship design files in module mode
          </span>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <strong>Import Format:</strong> Use the "Save as Module" option when designing ships to create compatible module files.
          </div>
          {modularPercent === 0 && (
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              <strong>Note:</strong> Set a modular percentage above to enable module import.
            </div>
          )}
        </div>

        <div className="space-y-2">
          {modules.map((mod, idx) => (
            <div key={idx} className="mb-2 w-full">
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={mod.name}
                  onChange={(e) =>
                    setModules(
                      modules.map((m, i) =>
                        i === idx ? { ...m, name: e.target.value } : m,
                      ),
                    )
                  }
                  className="w-40 px-2 py-1 border border-gray-300 rounded"
                  placeholder="Module Name"
                />
                <input
                  type="number"
                  min={0}
                  value={mod.tonnage}
                  onChange={(e) =>
                    handleModuleTonnageChange(idx, Number(e.target.value))
                  }
                  className="w-24 px-2 py-1 border border-gray-300 rounded"
                />
                <span className="text-xs text-gray-600">tons</span>
                <span className="text-xs text-gray-600">
                  Cost:{" "}
                  <b>{formatCurrency(mod.tonnage * MODULE_COST_PER_TON)}</b>
                </span>
                {mod.installed ? (
                  <button
                    className="ml-2 px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                    onClick={() => handleUninstallModule(idx)}
                  >
                    Uninstall
                  </button>
                ) : (
                  <button
                    className="ml-2 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    onClick={() => handleInstallModule(idx)}
                  >
                    Install
                  </button>
                )}
                <input
                  type="text"
                  value={mod.description || ""}
                  onChange={(e) =>
                    setModules(
                      modules.map((m, i) =>
                        i === idx ? { ...m, description: e.target.value } : m,
                      ),
                    )
                  }
                  className="flex-grow px-2 py-1 border border-gray-300 rounded"
                  placeholder="Module Description (optional)"
                  style={{ minWidth: "120px" }}
                />
                <button
                  className="ml-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  onClick={() => handleRemoveModule(idx)}
                >
                  Remove
                </button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-40" />
                {/* Fit-out Weight input (under Weight column) */}
                <input
                  type="number"
                  min={0}
                  value={mod.fitoutWeight || ""}
                  onChange={(e) =>
                    setModules(
                      modules.map((m, i) =>
                        i === idx
                          ? { ...m, fitoutWeight: Number(e.target.value) }
                          : m,
                      ),
                    )
                  }
                  className="w-36 px-2 py-1 border border-gray-300 rounded"
                  placeholder="Fit-out tons"
                  title="Additional fit-out weight (tons)"
                />
                {/* Fit-out Weight label (under Cost column) */}
                <span
                  className="text-xs text-gray-600"
                  style={{ width: "10rem", display: "inline-block" }}
                >
                  Fit-out Weight (Tons)
                </span>
                {/* Fit-out Cost input (under Install column) */}
                <input
                  type="number"
                  min={0}
                  value={mod.fitoutCost || ""}
                  onChange={(e) =>
                    setModules(
                      modules.map((m, i) =>
                        i === idx
                          ? { ...m, fitoutCost: Number(e.target.value) }
                          : m,
                      ),
                    )
                  }
                  className="w-42 px-2 py-1 border border-gray-300 rounded"
                  placeholder="Fit-out Cr"
                  title="Additional fit-out cost (Cr)"
                />
                {/* Fit-out Cost label (under Description column) */}
                <span
                  className="text-xs text-gray-600"
                  style={{ width: "10rem", display: "inline-block" }}
                >
                  Fit-out Cost (Cr)
                </span>
                {/* Fit-out Power input (under Power column) */}
                <input
                  type="number"
                  min={0}
                  value={mod.fitoutPower || ""}
                  onChange={(e) =>
                    setModules(
                      modules.map((m, i) =>
                        i === idx
                          ? { ...m, fitoutPower: Number(e.target.value) }
                          : m,
                      ),
                    )
                  }
                  className="w-36 px-2 py-1 border border-gray-300 rounded"
                  placeholder="Fit-out Power"
                  title="Additional fit-out power (Power)"
                />
                {/* Fit-out Power label (under Power column) */}
                <span
                  className="text-xs text-gray-600"
                  style={{ width: "10rem", display: "inline-block" }}
                >
                  Fit-out Power
                </span>
              </div>
              {/* Show comprehensive module config info if available */}
              {mod.moduleConfig && (
                <div className="mt-1 ml-40 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  <div className="font-medium text-gray-700 mb-1">📦 Imported Module Details:</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>Optional Systems: {mod.moduleConfig.optionalSystems?.length || 0}</div>
                    <div>Weapons: {mod.moduleConfig.weapons?.length || 0}</div>
                    <div>Weapon Mounts: {mod.moduleConfig.weaponMounts?.length || 0}</div>
                    <div>Power Required: {mod.moduleConfig.totalPower || 0} Power</div>
                    <div>Total Cost: {formatCurrency(mod.moduleConfig.totalCost || 0)}</div>
                    <div>Remaining Tons: {mod.moduleConfig.remainingTons || 0}</div>
                    {mod.moduleConfig.crewRequirements && (
                      <div>Crew Required: {mod.moduleConfig.crewRequirements}</div>
                    )}
                    {mod.moduleConfig.totalCrew && (
                      <div>Total Crew: {mod.moduleConfig.totalCrew}</div>
                    )}
                    {mod.moduleConfig.totalPassengers && (
                      <div>Passengers: {mod.moduleConfig.totalPassengers}</div>
                    )}
                    {mod.moduleConfig.computer?.length > 0 && (
                      <div>Computers: {mod.moduleConfig.computer.length}</div>
                    )}
                    {mod.moduleConfig.software?.installed?.length > 0 && (
                      <div>Software: {mod.moduleConfig.software.installed.length} installed</div>
                    )}
                    {mod.moduleConfig.drives?.length > 0 && (
                      <div>Drives: {mod.moduleConfig.drives.length}</div>
                    )}
                    {mod.moduleConfig.powerPlant?.length > 0 && (
                      <div>Power Plants: {mod.moduleConfig.powerPlant.length}</div>
                    )}
                    {mod.moduleConfig.staterooms?.length > 0 && (
                      <div>Staterooms: {mod.moduleConfig.staterooms.length}</div>
                    )}
                    {mod.moduleConfig.techLevel && (
                      <div>Tech Level: {mod.moduleConfig.techLevel}</div>
                    )}
                    {mod.moduleConfig.supplyUnits && (
                      <div>Supply Units: {mod.moduleConfig.supplyUnits}</div>
                    )}
                  </div>
                  {/* Show computer options if any */}
                  {mod.moduleConfig.computerOptions && Object.keys(mod.moduleConfig.computerOptions).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">💻 Computer Options:</div>
                      <div className="text-xs">
                        {Object.keys(mod.moduleConfig.computerOptions).map(computerId => {
                          const options = mod.moduleConfig.computerOptions[computerId];
                          return `Computer ${computerId}: ${Array.isArray(options) ? options.join(', ') : options}`;
                        }).join('; ')}
                      </div>
                    </div>
                  )}
                  {/* Show detailed optional systems if any */}
                  {mod.moduleConfig.optionalSystems && mod.moduleConfig.optionalSystems.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">🔧 Optional Systems Details:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.optionalSystems.map((system: any, index: number) => (
                          <div key={system.id || index} className="flex justify-between items-start">
                            <span className="font-medium">{system.name}</span>
                            <span className="text-gray-600">
                              {system.cost > 0 && `${formatCurrency(system.cost)}`}
                              {system.tonnage > 0 && ` | ${system.tonnage} tons`}
                              {system.power > 0 && ` | ${system.power} Power`}
                              {system.techLevel && ` | TL ${system.techLevel}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed drives if any */}
                  {mod.moduleConfig.drives && mod.moduleConfig.drives.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">🚀 Drive Systems:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.drives.map((drive: any, index: number) => (
                          <div key={drive.id || index} className="flex justify-between items-start">
                            <span className="font-medium">{drive.name}</span>
                            <span className="text-gray-600">
                              {drive.cost > 0 && `${formatCurrency(drive.cost)}`}
                              {drive.tonnage > 0 && ` | ${drive.tonnage} tons`}
                              {drive.power > 0 && ` | ${drive.power} Power`}
                              {drive.techLevel && ` | TL ${drive.techLevel}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed weapons if any */}
                  {mod.moduleConfig.weapons && mod.moduleConfig.weapons.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">⚔️ Weapons:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.weapons.map((weapon: any, index: number) => (
                          <div key={weapon.id || index} className="flex justify-between items-start">
                            <span className="font-medium">{weapon.name}</span>
                            <span className="text-gray-600">
                              {weapon.cost > 0 && `${formatCurrency(weapon.cost)}`}
                              {weapon.tons > 0 && ` | ${weapon.tons} tons`}
                              {weapon.power > 0 && ` | ${weapon.power} Power`}
                              {weapon.tl && ` | TL ${weapon.tl}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed weapon mounts if any */}
                  {mod.moduleConfig.weaponMounts && mod.moduleConfig.weaponMounts.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">🎯 Weapon Mounts:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.weaponMounts.map((mount: any, index: number) => (
                          <div key={index} className="flex justify-between items-start">
                            <span className="font-medium">{mount.type}</span>
                            <span className="text-gray-600">
                              {mount.cost > 0 && `${formatCurrency(mount.cost)}`}
                              {mount.tons > 0 && ` | ${mount.tons} tons`}
                              {mount.power > 0 && ` | ${mount.power} Power`}
                              {mount.weapons && mount.weapons.length > 0 && ` | ${mount.weapons.length} weapons`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed ammunition storage if any */}
                  {mod.moduleConfig.ammunitionStorage && mod.moduleConfig.ammunitionStorage.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">💣 Ammunition Storage:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.ammunitionStorage.map((ammo: any, index: number) => (
                          <div key={ammo.id || index} className="flex justify-between items-start">
                            <span className="font-medium">{ammo.name}</span>
                            <span className="text-gray-600">
                              {ammo.cost > 0 && `${formatCurrency(ammo.cost)}`}
                              {ammo.quantity > 0 && ` | Qty: ${ammo.quantity}`}
                              {ammo.tonsPerReload > 0 && ` | ${ammo.tonsPerReload} tons/reload`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed power plants if any */}
                  {mod.moduleConfig.powerPlant && mod.moduleConfig.powerPlant.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">⚡ Power Plants:</div>
                      <div className="text-xs space-y-1">
                        {mod.moduleConfig.powerPlant.map((plant: any, index: number) => (
                          <div key={plant.id || index} className="flex justify-between items-start">
                            <span className="font-medium">{plant.name}</span>
                            <span className="text-gray-600">
                              {plant.cost > 0 && `${formatCurrency(plant.cost)}`}
                              {plant.tons > 0 && ` | ${plant.tons} tons`}
                              {plant.power > 0 && ` | ${plant.power} Power`}
                              {plant.techLevel && ` | TL ${plant.techLevel}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show software details if any */}
                  {mod.moduleConfig.software && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <div className="font-medium text-gray-700 mb-1">🖥️ Software Details:</div>
                      {mod.moduleConfig.software.expertSkills && Object.keys(mod.moduleConfig.software.expertSkills).length > 0 && (
                        <div className="text-xs">Expert Skills: {Object.values(mod.moduleConfig.software.expertSkills).join(', ')}</div>
                      )}
                      {mod.moduleConfig.software.translatorLanguages && Object.keys(mod.moduleConfig.software.translatorLanguages).length > 0 && (
                        <div className="text-xs">Translator Languages: {Object.values(mod.moduleConfig.software.translatorLanguages).join(', ')}</div>
                      )}
                      {mod.moduleConfig.software.databaseTopics && Object.keys(mod.moduleConfig.software.databaseTopics).length > 0 && (
                        <div className="text-xs">Database Topics: {Object.values(mod.moduleConfig.software.databaseTopics).join(', ')}</div>
                      )}
                      {mod.moduleConfig.software.virtualCrewRoles && Object.keys(mod.moduleConfig.software.virtualCrewRoles).length > 0 && (
                        <div className="text-xs">Virtual Crew Roles: {Object.values(mod.moduleConfig.software.virtualCrewRoles).flat().join(', ')}</div>
                      )}
                      {mod.moduleConfig.software.virtualGunnerRoles && Object.keys(mod.moduleConfig.software.virtualGunnerRoles).length > 0 && (
                        <div className="text-xs">Virtual Gunner Roles: {Object.values(mod.moduleConfig.software.virtualGunnerRoles).flat().join(', ')}</div>
                      )}
                      {/* Show installed software packages */}
                      {mod.moduleConfig.software.installed && mod.moduleConfig.software.installed.length > 0 && (
                        <div className="text-xs mt-1">
                          <span className="font-medium">Installed Packages:</span>
                          {mod.moduleConfig.software.installed.map((pkg: any, index: number) => (
                            <span key={pkg.packageId || index} className="ml-1">
                              {pkg.packageId}{pkg.level > 0 ? `/${pkg.level}` : ''}
                              {index < mod.moduleConfig.software.installed.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={handleAddModule}
          disabled={
            totalInstalledTonnage >= modularTonnage || modularTonnage === 0
          }
        >
          Add Module
        </button>
        {installWarning && (
          <div className="mt-2 text-red-600 text-sm font-semibold">
            {installWarning}
          </div>
        )}
        <div className="mt-4 text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          <div>
            <b>Total Installed Module Tonnage:</b> {totalInstalledTonnage} /{" "}
            {modularTonnage} tons
          </div>
          <div>
            <b>Total Installed Module Cost:</b>{" "}
            {formatCurrency(totalInstalledCost)}
          </div>
          <div>
            <b>Total Module Cost:</b> {formatCurrency(totalModuleCost)}
          </div>
          <div>
            <b>Total Installed Fit-out Power:</b>{" "}
            {modules
              .filter((m) => m.installed)
              .reduce((sum, m) => sum + (m.fitoutPower || 0), 0)}{" "}
            Power
          </div>
        </div>
        <div className="flex flex-row gap-2 mt-4">
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={handleSaveConfig}
            disabled={modularPercent === 0}
          >
            {existingConfig ? "Update" : "Add"}
          </button>
          {existingConfig && (
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={handleRemoveConfig}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModularHullSystems;
